import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild, Output, EventEmitter } from '@angular/core';
import { CatalogService } from '@shared/services/catalog.service';
import { map, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { CatalogParams, CatalogResponse, CatalogResponseInfo } from '@shared/models/catalog.service';
import { CustomIcons } from '@shared/models/custom-icons';
import { Store } from '@ngxs/store';
import { SharedState } from '@shared/state/shared/shared.state';
import { getStringFilter } from '@utils/catalog-params';
import { MultiSelectComponent } from '@shared/components/multi-select/multi-select.component';
import { SelectOption } from '@shared/components/multi-select/models';
import { TranslateService } from '@ngx-translate/core';
import { Locale } from '@shared/models/locale';
import { CatalogImpairedView, DataFilterForm } from '../../models/table';
import { DataState } from '../../data-state.service';
import { GetCatalogsExportStatus } from '../../../../../data/store/catalog/catalog.action';
import { SortingType } from '../../../../../data/modules/catalog-data/components/ordering/models';
import { ConnectedPosition } from "@angular/cdk/overlay";

@Component({
  selector: 'app-filter-table',
  templateUrl: './filter-table.component.html',
  styleUrls: ['./filter-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FilterTableComponent implements OnInit {
  public countSelectProviders = 0;
  public countSelectCategories = 0;
  public catalogs$: Observable<CatalogResponse>;
  public icons = CustomIcons;
  public catalogCount: number;
  public catalogs: CatalogImpairedView[] = [];
  public isOpenFilter = false;
  public hasFilter = false;
  public isActiveCategoryFilter = false;
  public isActiveProviderFilter = false;
  public categories$: Observable<SelectOption[]> = this.dataState.categories.pipe(
    tap(categories => {
      this.countSelectCategories = categories.filter(x => x.isSelected).length;
      this.setCatalogObs();
    })
  );
  public providers$: Observable<SelectOption[]> = this.dataState.providers.pipe(
    tap(providers => {
      this.countSelectProviders = providers.filter(x => x.isSelected).length;
      this.setCatalogObs();
    })
  );

  public categoriesList$ = this.store.select(SharedState.getCategories).pipe(
    map(categories => {
      if (this.dataState.categories.value.length === 0) {
        this.dataState.categories.next(
          categories.map(c => ({
            value: c.idOpenCategory,
            title: `${c.fullName} (${c.catalogsCount})`,
            isSelected: true
          }))
        );
      }
    })
  );

  public providersList$ = this.store.select(SharedState.getProviders).pipe(
    tap(providers => {
      if (this.dataState.providers.value.length === 0) {
        this.dataState.providers.next(
          providers.map(c => ({
            value: c.idOIV,
            title: `${c.fullNameOIV} (${c.openCatalogCount})`,
            isSelected: true
          }))
        );
      }
    })
  );

  @Output() public readonly resetPaginator = new EventEmitter<number>();
  @ViewChild('categoriesSelect') public categoriesSelect: MultiSelectComponent;
  @ViewChild('providerSelect') public providerSelect: MultiSelectComponent;

  public paginatorValue: {
    limit: number;
    offset: number;
  } | null = {
    limit: 10,
    offset: 0
  };

  public filterForm: DataFilterForm;

  public filterForm$ = this.dataState.filterForm.pipe(
    tap(res => {
      this.filterForm = res;
      this.setCatalogObs();
    })
  );
  public filterOverlayPosition: ConnectedPosition[] = [
    {
      originX: 'center',
      originY: 'top',
      overlayX: 'center',
      overlayY: 'top'
    }
  ];

  public dropDownOverlayPosition: ConnectedPosition[] = [
    {
      originX: 'center',
      originY: 'bottom',
      overlayX: 'center',
      overlayY: 'top'
    }
  ];

  public sortingDataSet$: Observable<SortingType>;
  constructor(
    private catalogService: CatalogService,
    private cdr: ChangeDetectorRef,
    private store: Store,
    private dataState: DataState,
    private translateService: TranslateService
  ) {}

  private convertCatalog(catalogRespose: CatalogResponseInfo): CatalogImpairedView {
    return {
      id: catalogRespose.idPublicCatalog,
      archive: catalogRespose.publicCatalogStatus === 'Архивный',
      categoryId: catalogRespose.idOpenCategory,
      categoryName: catalogRespose.nameOpenCategory,
      categoryNameEn: catalogRespose.nameOpenCategoryEn,
      hasGeo: catalogRespose.hasGeo,
      name: catalogRespose.namePublicCatalog,
      nameEn: catalogRespose.namePublicCatalogEn,
      isDynamic: catalogRespose.isDynamic,
      providerId: catalogRespose.idOIV,
      providerName: catalogRespose.nameOIV,
      providerNameEn: catalogRespose.nameOIVEn.trim() !== '' ? catalogRespose.nameOIVEn : null
    };
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  private setCatalogObs(hasChangePaginator = true): void {
    const selectCategories = (this.dataState.categories.value ?? []).filter(x => x.isSelected).map(x => +(x.value ?? 0));
    const selectProviders = (this.dataState.providers.value ?? []).filter(x => x.isSelected).map(x => +(x.value ?? 0));
    this.hasFilter = false;
    if (selectCategories.length === 0 || selectProviders.length === 0) {
      this.catalogCount = 0;
      this.catalogs = [];
      this.hasFilter = true;
      if (hasChangePaginator) {
        this.resetPaginator.emit(0);
      }
      return;
    }

    const objectFormTrueValuesLength = Object.values(this.filterForm).filter(x => x).length;

    if (
      selectCategories.length !== this.dataState.categories.value.length ||
      selectProviders.length !== this.dataState.providers.value.length ||
      this.filterForm.newRecommendedFavorites ||
      objectFormTrueValuesLength > 1 ||
      (objectFormTrueValuesLength === 1 && !this.filterForm.activeStatus)
    ) {
      this.hasFilter = true;
    }

    const criteria = getStringFilter(
      {
        booleanFilters: this.filterForm,
        openCategoryIds: selectCategories,
        providerIds: selectProviders,
        newRecommendedFavorites: this.filterForm.newRecommendedFavorites ? 'new' : 'all'
      },
      this.translateService.currentLang as Locale
    );

    let params: CatalogParams = {
      criteria: criteria
    };
    if (this.paginatorValue) {
      params = {
        ...params,
        ...this.paginatorValue,
        sorting: this.dataState.sorting.value
      };
    }

    this.catalogs$ = this.catalogService.getCatalogs(params).pipe(
      tap(res => {
        this.catalogCount = res.found;
        this.catalogs = res.response.map(catalogRespose => this.convertCatalog(catalogRespose));
        this.store.dispatch(new GetCatalogsExportStatus(this.catalogs.map(x => x.id)));
        if (hasChangePaginator) {
          this.resetPaginator.emit(res.found);
        }
      })
    );
    this.cdr.detectChanges();
  }

  public ngOnInit(): void {
    this.sortingDataSet$ = this.dataState.sorting.pipe(
      tap(() => {
        this.setCatalogObs(false);
      })
    );
  }

  public paginatorChanged(value: { pageIndex: number; pageSize: number }): void {
    if (value.pageSize > 0) {
      this.paginatorValue = {
        limit: value.pageSize,
        offset: value.pageSize * (value.pageIndex - 1)
      };
    } else {
      this.paginatorValue = null;
    }
    this.setCatalogObs(false);
  }

  public onChooseProviders(providerOptions: boolean[]): void {
    const providers = this.dataState.providers.value;
    providerOptions.forEach((isChosen, index) => {
      providers[index].isSelected = isChosen;
    });

    this.dataState.providers.next(providers);
  }

  public onChooseCategories(categoryOptions: boolean[]): void {
    const categories = this.dataState.categories.value;
    categoryOptions.forEach((isChosen, index) => {
      categories[index].isSelected = isChosen;
    });

    this.dataState.categories.next(categories);
    this.setCatalogObs();
  }

  public clearFilter(): void {
    this.providerSelect.choseAllOptions(true);
    this.categoriesSelect.choseAllOptions(true);
    this.dataState.setDefaultFilterForm();
  }
}
